import type { PosReportReadResult, TemplateLine } from '../types';
import { lineKeyFor, sameName } from './glOneList';

/**
 * What a POS report prints, as the screens offer it (ptc-docs plan 2026-11-02 wave 4 §3–§4, §6): **a copy of core's
 * registry v0.3, FINAL** (ptc-docs `coordination/plans/2026-10-29-extraction-registry-v0.3.md` §2 and §4, finalised
 * 2026-11-02 at `4495b1eb`), mirrored on the server by `api/Services/PosReportRegistry.cs`. The codes are BINDING: a
 * change is v0.4, never an edit here alone. This file is replaced by the engine's own registry when PtcCore.Extraction 0.2.0 is adopted (P2.5). `info.*` codes are never
 * offered: they never post (registry §1). The section and line labels are the report's printed words, so they are not
 * translated.
 */
export const REGISTRY_VERSION = '0.3';

export interface RegistryLine {
  label: string;
  code: string;
}

export interface RegistrySection {
  section: string;
  lines: RegistryLine[];
}

/** SpotOn's Financial Overview, in print order (§7.1), the posting codes only. */
export const SPOTON_SECTIONS: readonly RegistrySection[] = [
  {
    section: 'Net Sales by Revenue Class', // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
    lines: [
      { label: 'Beer', code: 'sales.alcohol.beer' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      { label: 'Beverage', code: 'sales.beverage.nonAlcoholic' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      { label: 'Food', code: 'sales.food' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      { label: 'Liquor', code: 'sales.alcohol.liquor' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      // §7.2.1: the seventh class IS the surcharge (= Dual Pricing = the Surcharges department): one leaf, not two.
      { label: 'Non-Sales Revenue', code: 'sales.surcharge' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      { label: 'Retail', code: 'sales.merchandise' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      { label: 'Wine', code: 'sales.alcohol.wine' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      { label: 'Total Net Sales', code: 'sales.net' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
    ],
  },
  { section: 'Gross Sales by Revenue Class', lines: [{ label: 'Total Gross Sales', code: 'sales.gross' }] }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
  // §7.2.3: one tax line on this report, so tax.collected only.
  { section: 'Tax Summary', lines: [{ label: 'Sales Tax', code: 'tax.collected' }] }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
  {
    section: 'Tip Summary', // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
    lines: [
      { label: 'Credit', code: 'tips.collected' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      // DP-P2-4 (ruled 2026-10-30): an automatic gratuity is a service charge, never tips payable.
      { label: 'Gratuity', code: 'sales.serviceCharge' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
    ],
  },
  {
    section: 'Payment Summary', // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
    lines: [
      { label: 'Cash', code: 'tender.cash' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      { label: 'Credit', code: 'tender.card' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      // v0.3 FINAL: the refunds line, signed as printed; card settlement reads it with Credit (registry §4).
      { label: 'Credit Refund', code: 'tender.cardRefund' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
    ],
  },
  {
    section: 'Cash Summary', // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
    lines: [
      { label: 'Tips Paid', code: 'tips.paidOut' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      { label: 'Safe Drops', code: 'cash.safeDrop' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
      { label: 'Final Deposit', code: 'cash.finalDeposit' }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
    ],
  },
  { section: 'Liabilities', lines: [{ label: 'Gift Card Sales', code: 'giftcard.activation' }] }, // i18n-ignore: the report's own printed words (registry §7.1), the same in every language
];

const BY_CODE: ReadonlyMap<string, { section: string; label: string }> = new Map(
  SPOTON_SECTIONS.flatMap((s) => s.lines.map((l) => [l.code, { section: s.section, label: l.label }] as const)),
);

/** Where a code is printed, or undefined for a code SpotOn's report does not print (fees.merchant, a DR-15 code). */
export const printedAt = (code: string) => BY_CODE.get(code);

/** The words a set of picks is said in: "Net Sales by Revenue Class › Beer + Liquor + Wine; Tax Summary › Sales Tax". */
export function hintFor(codes: readonly string[]): string {
  const bySection = new Map<string, string[]>();
  const other: string[] = [];
  for (const code of codes) {
    const at = BY_CODE.get(code);
    if (!at) {
      other.push(code);
      continue;
    }
    bySection.set(at.section, [...(bySection.get(at.section) ?? []), at.label]);
  }
  return [...[...bySection].map(([section, labels]) => `${section} › ${labels.join(' + ')}`), ...other].join('; ');
}

/** The month a printed period posts to: its start (SpotOn's 09/01 10:00 AM → 10/01 10:00 AM is September). */
export function periodMonth(period: PosReportReadResult['period'], locale: string): string | null {
  const m = period?.printedStart.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (!m) return null;
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(Number(m[3]), Number(m[1]) - 1, 1)),
  );
}

export type Preview =
  | { kind: 'noReport' }
  | { kind: 'notReadYet'; report: string }
  | { kind: 'unknownKind' }
  | { kind: 'nothingPicked' }
  | { kind: 'notPrinted'; codes: string[] }
  | { kind: 'reads'; amount: number; month: string | null };

/**
 * What a line's picks would read from the client's latest POS report (wave 4 §4). Never a blank: no report, a report not
 * read yet, a kind not in the registry, or a pick the report does not print are each said. Amounts are decimal strings,
 * summed in cents.
 */
export function previewOf(codes: readonly string[], read: PosReportReadResult | null | undefined, locale: string): Preview {
  if (!read) return { kind: 'noReport' };
  if (read.status === 'unknownKind') return { kind: 'unknownKind' };
  if (read.status === 'notReadYet') return { kind: 'notReadYet', report: read.kindLabel ?? '' };
  if (codes.length === 0) return { kind: 'nothingPicked' };
  const missing = codes.filter((c) => !read.fields.some((f) => f.code === c));
  if (missing.length) return { kind: 'notPrinted', codes: missing };
  const cents = codes.reduce((sum, c) => sum + Math.round(Number(read.fields.find((f) => f.code === c)!.amount) * 100), 0);
  return { kind: 'reads', amount: cents / 100, month: periodMonth(read.period, locale) };
}

/** A sales class line of the standard template: its id (its name is `glMappingPage.template.standard.classes.<id>`), and the codes it reads. */
interface StandardClass {
  id: string;
  codes: string[];
}

/** The standard restaurant/POS template's sales classes (wave 4 §3), one line each; its other lines are the standard roles. */
export const STANDARD_SALES_CLASSES: readonly StandardClass[] = [
  { id: 'food', codes: ['sales.food'] },
  { id: 'nonAlcoholic', codes: ['sales.beverage.nonAlcoholic'] },
  { id: 'alcoholic', codes: ['sales.alcohol.beer', 'sales.alcohol.liquor', 'sales.alcohol.wine'] },
  { id: 'surcharge', codes: ['sales.surcharge'] },
  { id: 'retail', codes: ['sales.merchandise'] },
];

/** The standard template's role lines, after the sales classes: the six POS roles, then the allowance from the return. */
const STANDARD_ROLE_LINES: readonly TemplateLine[] = [
  { key: 'salesTaxPayable', side: 'credit', source: 'pos', registryField: ['tax.collected'] },
  { key: 'tipsPayable', side: 'credit', source: 'pos', registryField: ['tips.collected'] },
  // Card settlement is net of refunds (registry v0.3 FINAL §4).
  { key: 'creditCardClearing', side: 'debit', source: 'pos', registryField: ['tender.card', 'tender.cardRefund'] },
  { key: 'cashOnHand', side: 'debit', source: 'pos', registryField: ['tender.cash'] },
  { key: 'merchantFees', side: 'debit', source: 'pos', registryField: ['fees.merchant'] },
  {
    key: 'collectionAllowanceIncome', side: 'credit', source: 'salesTaxReturn', offsetKey: 'salesTaxPayable',
    registryField: ['return.dr15.collectionAllowance'],
  },
];

export interface Proposal {
  lines: TemplateLine[];
  rowLabels: Record<string, string>;
  /** How many lines the proposal added; 0 = the template already had them all. */
  added: number;
}

/** A line already reads a code when it picks it (a line with no picks reads nothing the proposal can see). */
const readsCode = (line: TemplateLine, code: string) => (line.registryField ?? []).includes(code);

function addClassLine(
  out: Proposal, label: string, codes: string[], roles: Readonly<Record<string, string>>,
) {
  const { key } = lineKeyFor(label, out.rowLabels, [...Object.keys(roles), ...Object.keys(out.rowLabels), ...out.lines.map((l) => l.key)]);
  const there = out.lines.find((l) => l.key === key || sameName(l.label ?? '') === sameName(label));
  if (there) {
    // The client's own line of that name is kept as it is, but learns the picks it has none of.
    if (!(there.registryField ?? []).length) Object.assign(there, { registryField: codes, hint: hintFor(codes) });
    return;
  }
  out.rowLabels = { ...out.rowLabels, [key]: label };
  out.lines.push({ key, label, side: 'credit', source: 'pos', registryField: codes, hint: hintFor(codes) });
  out.added++;
}

/**
 * "Start from a standard restaurant/POS template" (wave 4 §3): the standard lines, into the FORM (unsaved; reviewed, then
 * saved). Lines the template already has are kept as they are, and nothing is removed. A sales class reuses the client's
 * line of the same name (one key per name), and its account is `roles[key]`, so a line the client has mapped arrives mapped.
 */
export function proposeStandardTemplate(
  current: readonly TemplateLine[], roles: Readonly<Record<string, string>>, rowLabels: Readonly<Record<string, string>>,
  nameOf: (classId: string) => string,
): Proposal {
  const out: Proposal = { lines: current.map((l) => ({ ...l })), rowLabels: { ...rowLabels }, added: 0 };
  // A client already reading a class's codes on a line of its own (Beer + Liquor + Wine on one) does not get that class again.
  for (const c of STANDARD_SALES_CLASSES) {
    if (c.codes.every((code) => out.lines.some((l) => readsCode(l, code)))) continue;
    addClassLine(out, nameOf(c.id), c.codes, roles);
  }
  for (const line of STANDARD_ROLE_LINES) {
    if (out.lines.some((l) => l.key === line.key)) continue;
    out.lines.push({ ...line });
    out.added++;
  }
  return out;
}

/**
 * "Add lines from this report" (wave 4 §3): one sales line per revenue class the report PRINTS that no line reads yet, its
 * pick and hint set. A class the registry has no code for is not proposed (it is said instead, by the caller).
 */
export function proposeFromReport(
  current: readonly TemplateLine[], roles: Readonly<Record<string, string>>, rowLabels: Readonly<Record<string, string>>,
  classesPrinted: PosReportReadResult['classesPrinted'],
  nameOf: (printedLabel: string) => string,
): Proposal {
  const out: Proposal = { lines: current.map((l) => ({ ...l })), rowLabels: { ...rowLabels }, added: 0 };
  for (const c of classesPrinted) {
    if (!c.code || out.lines.some((l) => readsCode(l, c.code!))) continue;
    addClassLine(out, nameOf(c.label), [c.code], roles);
  }
  return out;
}

/** "$3,983.00" in the screen's language: a POS report's figures are dollars. */
export const formatUsd = (amount: number, locale: string): string =>
  new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(amount);

/**
 * The template lines a READ report fills (wave 4 §5, lights up at P2.5): each line whose every pick the report emitted gets
 * the picks' sum (cents); a line with no picks, or a pick the report did not emit, is left for the person. Amounts are
 * decimal strings, never floats.
 */
export function figuresFromFields(
  lines: readonly TemplateLine[], fields: PosReportReadResult['fields'],
): { figures: Record<string, string>; filled: number; left: number } {
  const figures: Record<string, string> = {};
  let left = 0;
  for (const line of lines) {
    const picks = line.registryField ?? [];
    if (picks.length === 0 || !picks.every((c) => fields.some((f) => f.code === c))) {
      left++;
      continue;
    }
    const cents = picks.reduce((sum, c) => sum + Math.round(Number(fields.find((f) => f.code === c)!.amount) * 100), 0);
    figures[line.key] = (cents / 100).toFixed(2);
  }
  return { figures, filled: Object.keys(figures).length, left };
}
